import React, { FC, useContext, useState } from 'react';
import { SortDirection, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { StyledLink } from '../Components';
import { useQuery } from '@apollo/client';
import LoadingSpinner from '../LoadingSpinner';
import { GET_REVISIONS_WITH_USER } from './home.graphql';
import { Unpacked } from '../graphQLTypes/types';
import { GetRevisionsWithUserQuery } from '../__generated__/graphql';
import { StyledPaper } from '../theme';
import { Lens } from '@mui/icons-material';
import { TableCellSort, ColWidths, TableCellEllipsis, TableCellHead, TableCellIcon } from '../components/TableComponents';
import { getDefaultComparer } from '../resources/ResourceTable';
import { getAllUsersIdsWithRolesForRevision, roles, getRoles } from '../resources/resourceUtils';
import { RevisionColumn, getCompareFn } from '../resources/RoleHeaders';
import { useUserContext } from '../UserContext';

type Revision = Unpacked<GetRevisionsWithUserQuery['revisions']>;

export const MyParticipationInRevisions: FC = () => {
  const { data, loading } = useQuery(GET_REVISIONS_WITH_USER);
  return (
    <StyledPaper>
      <Typography variant="h5" component="h3" style={{ paddingBottom: 15 }}>
        Mine prosjektroller (anvisninger under revisjon)
      </Typography>
      {loading && <LoadingSpinner />}
      {data?.revisions && <MyParticipationTable revisions={data.revisions} />}
      <div style={{ paddingTop: 10 }}>
        <StyledLink to="/resource?me=1">Gå til ressurser</StyledLink>
      </div>
    </StyledPaper>
  );
};

const MyParticipationTable: FC<{ revisions: Revision[] }> = ({ revisions }) => {
  const { userId } = useUserContext();

  const [orderBy, setOrderBy] = useState<RevisionColumn | false>(false);
  const [direction, setDirection] = useState<SortDirection>(false);

  const handleSortClick = (key: RevisionColumn) => {
    if (orderBy === key) {
      setDirection(direction === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(key);
      setDirection('asc');
    }
  };

  const sortedRevisions = revisions
    .filter((x) => getAllUsersIdsWithRolesForRevision(x).some((r) => r?.user?.id === userId) || x?.guide?.owner?.id === userId)
    .sort(orderBy ? getCompareFn(orderBy, direction) : getDefaultComparer([userId!]));

  const ColumnHeader = ({ colKey, inputProps, children }: { colKey: RevisionColumn; inputProps?: any; children?: any }) => (
    <TableCellSort inputProps={{ ...inputProps }} active={orderBy === colKey} sortDirection={direction} onClick={() => handleSortClick(colKey)}>
      {children}
    </TableCellSort>
  );

  return (
    <Table size="small">
      <ColWidths widths={[80, 250, null, null, null, null, null, null, null, null, 80, 150]}></ColWidths>
      <TableHead>
        <TableRow>
          <ColumnHeader colKey="number">Nummer</ColumnHeader>
          <ColumnHeader colKey="title">Tittel</ColumnHeader>
          <RoleHeaders />
          <ColumnHeader colKey="progress">Fremdrift</ColumnHeader>
          <ColumnHeader colKey="currentPhase">Fase</ColumnHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedRevisions.map((r) => (
          <TableRow key={r?.id}>
            <TableCell style={{ paddingRight: 16 }} title={r?.guide?.docName ?? undefined}>
              <StyledLink to={`/revision/${r?.id}`}>
                <span>{r?.guide?.docName}</span>
              </StyledLink>
            </TableCell>
            <TableCellEllipsis title={r?.guide?.docTitle ?? undefined}>{r?.guide?.docTitle}</TableCellEllipsis>
            <Roles revision={r} userId={userId} />
            <TableCell>{`${r?.progress}%`} </TableCell>
            <TableCellEllipsis>{r?.currentPhase} </TableCellEllipsis>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const RoleHeaders: FC = () => {
  return (
    <>
      {Object.entries(roles).map(([key, val]) => (
        <TableCellHead key={key} sx={{ width: 100, paddingBottom: 0 }}>
          <Tooltip title={val.title}>
            <span>{val.label}</span>
          </Tooltip>
        </TableCellHead>
      ))}
    </>
  );
};

const Roles: FC<{ revision: Revision; userId?: string }> = ({ revision, userId }) => {
  return (
    <>
      {getRoles(revision).map((r) => {
        const isSelectedUser = r.userIds.includes(userId ?? '');
        return (
          <TableCellIcon key={r.role}>
            <div>{isSelectedUser && <Lens style={{ fontSize: 10 }} />}</div>
          </TableCellIcon>
        );
      })}
    </>
  );
};
